import React, { useEffect, useRef, useState } from "react";
import RoomData from "../../../interfaces/robot/rooms/room-data";
import { useSharedActions, useSharedDispatch, useSharedSelector } from "../../../store/SharedProvider";
import { selectLimitZonesData, selectSelectedRoomLimitZone } from "../../../store/room/room-selectors";
import { selectZonePlannerMouseState } from "../../../store/room/zone-planner/zone-planner-selectors";
import ZoneControllerMouseState from "../../../enums/robot/zone-planner/zone-controller-mouse-state";
import { getSvgPointFromMouseClick } from "../../../utils/map/svg-helper";
import { getRosPointFromSvgCoordinates, setPointInRoom } from "../../../interfaces/robot/rooms/point";
import RoomLimitZoneSvgPolygon from "./RoomLimitZoneSvgPolygon";
import HttpFetchStatus from "../../../enums/api/http-fetch-status";
import { deleteCursorSvg, draggingCursor, grabCursor } from "./ZonePlannerMapZones";

const ZonePlannerMapRoomLimitZone: React.FC<{ roomData: RoomData; zoomScale: number }> = ({ roomData, zoomScale }) => {
  const dispatch = useSharedDispatch();

  const {
    updateRoomLimitZoneCoordinatesOnDrag,
    updateRoomLimitZonePointCoordinates,
    setRoomLimitZoneSelectedPointId,
    patchLimitZone,
    setPreviousRoomLimitZone,
  } = useSharedActions();

  const limitZone = useSharedSelector((state) => selectSelectedRoomLimitZone(state));
  const limitZonesData = useSharedSelector((state) => selectLimitZonesData(state));
  const zonePlannerMouseState = useSharedSelector((state) => selectZonePlannerMouseState(state));

  const svgRef = useRef<SVGSVGElement>(null);
  const [hasChanged, setHasChanged] = useState(false);

  useEffect(() => {
    if (limitZonesData.rest.patchStatus === HttpFetchStatus.Success) {
      setHasChanged(false);
    }
  }, [limitZonesData.rest.patchStatus]);

  const dragZone = (event: React.MouseEvent | React.TouchEvent) => {
    const svgPoint = getSvgPointFromMouseClick(event, svgRef, zoomScale);
    const rosPoint = getRosPointFromSvgCoordinates(svgPoint!.x, svgPoint!.y, roomData);
    if (rosPoint == null) {
      return;
    }
    setHasChanged(true);
    dispatch(
      updateRoomLimitZoneCoordinatesOnDrag({
        initialDragPoint: limitZonesData.prevDragPoint == null ? rosPoint : limitZonesData.prevDragPoint,
        newDragPoint: rosPoint,
        roomData,
      }),
    );
  };

  const svgMouseTouchMoveHandler = (event: React.MouseEvent | React.TouchEvent) => {
    // drag zone if mouse is down
    if (
      "buttons" in event &&
      event.buttons === 1 &&
      zonePlannerMouseState === ZoneControllerMouseState.DragRoomLimitationZone
    ) {
      dragZone(event);
    }

    // update selected point coordinates (effectively drag the point)
    const svgPoint = getSvgPointFromMouseClick(event, svgRef, zoomScale);
    let updatedPoint = getRosPointFromSvgCoordinates(svgPoint!.x, svgPoint!.y, roomData);
    if (updatedPoint) {
      updatedPoint = setPointInRoom(updatedPoint, roomData);
    }
    if (updatedPoint != null) {
      setHasChanged(true);
      dispatch(
        updateRoomLimitZonePointCoordinates({
          pointId: limitZonesData.selectedPointId,
          newPoint: updatedPoint,
        }),
      );
    }
  };

  const svgMouseUpHandler = (event: React.MouseEvent) => {
    event.preventDefault();
    mouseUpEndActionHandler();
  };

  const svgTouchEndHandler = (_event: React.TouchEvent) => {
    document.body.style.overscrollBehavior = "";
    mouseUpEndActionHandler();
  };

  const mouseUpEndActionHandler = () => {
    if (limitZone != null && hasChanged) {
      dispatch(patchLimitZone({ id: limitZone.id, data: { points: limitZone.points } }));
    }
    dispatch(setRoomLimitZoneSelectedPointId(null));
  };

  if (
    limitZone == null ||
    !(
      zonePlannerMouseState === ZoneControllerMouseState.EditRoomLimitation ||
      zonePlannerMouseState === ZoneControllerMouseState.DragRoomLimitationZone ||
      zonePlannerMouseState === ZoneControllerMouseState.DeleteRoomLimitationZonePoint
    )
  ) {
    return null;
  }

  let cursorStyle = "";
  if (
    zonePlannerMouseState === ZoneControllerMouseState.DragRoomLimitationZone &&
    limitZonesData.prevDragPoint != null
  ) {
    cursorStyle = draggingCursor;
  } else if (zonePlannerMouseState === ZoneControllerMouseState.DragRoomLimitationZone) {
    cursorStyle = grabCursor;
  } else if (zonePlannerMouseState === ZoneControllerMouseState.DeleteRoomLimitationZonePoint) {
    cursorStyle = deleteCursorSvg;
  }

  return (
    <svg
      viewBox={`0 0 ${roomData.mapInformation.width} ${roomData.mapInformation.height}`}
      ref={svgRef}
      onMouseMove={svgMouseTouchMoveHandler}
      onTouchMove={svgMouseTouchMoveHandler}
      onMouseUp={svgMouseUpHandler}
      onTouchEnd={svgTouchEndHandler}
      onContextMenu={(e) => e.preventDefault()}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        height: roomData.mapInformation.height,
        width: roomData.mapInformation.width,
        cursor: cursorStyle,
      }}
    >
      <RoomLimitZoneSvgPolygon
        key={limitZone.id}
        roomData={roomData}
        scale={zoomScale}
        mouseState={zonePlannerMouseState}
      />
    </svg>
  );
};

export default ZonePlannerMapRoomLimitZone;
